'use client';
import React, { useRef } from 'react';
import { PortableText } from '@portabletext/react';
import { PortableTextBlock } from 'sanity';
import { components } from './PortableTextComponents';
import { m, useInView } from 'framer-motion';
import { fadeUp } from '@/assets/framer/animations';
import { IKButton } from '@/components/shared/material';
import { MenuItemLink } from '@/types/types';

type StatInfoItemType = {
  type: 'standard' | 'hellopeter';
  heading: string;
  description: PortableTextBlock;
};

export default function CompanyStatsHighlightPanel(props: {
  data?: {
    sectionId?: string;
    settings?: {
      heading?: PortableTextBlock;
      subheading?: PortableTextBlock;
      trustText?: PortableTextBlock;
      readMoreButton: {
        text: string;
        link: MenuItemLink;
      };
      stats: StatInfoItemType[];
    };
  };
}) {
  const headingRef = useRef(null);
  const section = useRef(null);

  const isInView = useInView(headingRef, { amount: 0.4, once: true });
  const sectionId = props.data?.sectionId;
  // These fields pull from global settings in the CMS
  const heading = props.data?.settings?.heading;
  const subheading = props.data?.settings?.subheading;
  const readMoreButton = props.data?.settings?.readMoreButton;
  const stats = props.data?.settings?.stats;

  return (
    <>
      <section
        id={sectionId}
        ref={section}
        className='bg-gradient-yellow section-padding relative z-10 mt-16 scroll-m-16 bg-gradient-to-b md:mt-0'
      >
        {/* Top Divider wave */}
        <div className='absolute left-0 top-0 h-max w-full -translate-y-[calc(100%_-_1px)] transform overflow-hidden'>
          <svg
            xmlns='http://www.w3.org/2000/svg'
            className='w-[200%] md:w-full [&_path]:fill-yellow'
            width='1464'
            height='34'
            viewBox='0 0 1464 34'
            fill='none'
            preserveAspectRatio='none'
          >
            <path
              d='M0 34H1464V13.9567C1462.14 14.0919 1459.76 14.2755 1456.86 14.4993L1456.79 14.5044C1400.39 18.8525 1148.67 38.2577 731.531 12.6924C315.789 -12.7874 65.0374 7.19974 7.26685 11.8046C4.35205 12.0369 1.92871 12.23 0 12.3763V34Z'
              fill='none'
            />
          </svg>
        </div>
        <div className='container-padding container mx-auto pb-20 lg:pb-24'>
          <div className='relative z-10 flex flex-col gap-10 md:flex-row md:gap-7'>
            <div className='relative space-y-2 md:w-1/2 md:space-y-5'>
              <m.h2
                initial='hidden'
                animate={isInView ? 'visible' : 'hidden'}
                variants={fadeUp}
                ref={headingRef}
                className='heading-xl relative z-10 max-w-sm font-display font-medium tracking-tighter text-charcoal sm:max-w-3xl'
              >
                {heading && (
                  <PortableText
                    components={components}
                    value={heading}
                  ></PortableText>
                )}
              </m.h2>

              <m.h3
                initial='hidden'
                animate={isInView ? 'visible' : 'hidden'}
                variants={fadeUp}
                transition={{ delay: 0.1 }}
                className='max-w-[550px] text-base text-charcoal 2xl:text-lg'
              >
                {subheading && (
                  <PortableText
                    components={components}
                    value={subheading}
                  ></PortableText>
                )}

                {readMoreButton &&
                  readMoreButton.link &&
                  readMoreButton.link.url &&
                  readMoreButton.text && (
                    <IKButton
                      className='group inline-flex items-center gap-2 !py-0 text-base font-bold 2xl:text-lg [&>a]:inline-flex'
                      link={readMoreButton.link}
                      size='sm'
                      variant='text'
                    >
                      <span>{readMoreButton.text}</span>
                      <svg
                        className='inline-block transition duration-500 group-hover:translate-x-2 [&_path]:fill-charcoal'
                        xmlns='http://www.w3.org/2000/svg'
                        width='17'
                        height='16'
                        viewBox='0 0 17 16'
                        fill='none'
                      >
                        <path
                          d='M9.81223 15.4466L16.2143 9.0506C16.3186 8.95578 16.4019 8.84051 16.4589 8.7121C16.5159 8.58369 16.5453 8.44494 16.5453 8.30466C16.5453 8.16438 16.5159 8.02564 16.4589 7.89723C16.4019 7.76881 16.3186 7.65354 16.2143 7.55872L9.81223 1.16268C9.62179 0.977924 9.37156 0.865835 9.10577 0.84623C8.83997 0.826625 8.57574 0.900765 8.35979 1.05554C8.23002 1.14171 8.12192 1.25621 8.04381 1.39025C7.96569 1.52429 7.91964 1.67429 7.9092 1.82873C7.89876 1.98316 7.9242 2.13791 7.98357 2.28108C8.04294 2.42424 8.13466 2.55201 8.25167 2.65456L12.8785 7.23732L1.47524 7.23732C1.19718 7.248 0.934093 7.36495 0.741174 7.56365C0.548254 7.76235 0.440491 8.02735 0.440491 8.30306C0.440491 8.57878 0.548254 8.84378 0.741174 9.04248C0.934093 9.24118 1.19718 9.35813 1.47524 9.3688L12.8801 9.3688L8.25328 13.9532C8.13627 14.0557 8.04455 14.1835 7.98518 14.3266C7.92581 14.4698 7.90037 14.6246 7.91081 14.779C7.92126 14.9334 7.96731 15.0834 8.04542 15.2175C8.12354 15.3515 8.23164 15.466 8.36141 15.5522C8.57736 15.707 8.84159 15.7811 9.10738 15.7615C9.37317 15.7419 9.62341 15.6298 9.81385 15.445L9.81223 15.4466Z'
                          fill='none'
                        />
                      </svg>
                    </IKButton>
                  )}
              </m.h3>
            </div>
            <div className='relative space-y-10 md:w-1/2 xl:space-y-12'>
              {stats &&
                stats.length > 0 &&
                stats.map((stat, index) => (
                  <m.div
                    initial='hidden'
                    animate={isInView ? 'visible' : 'hidden'}
                    transition={{
                      delay: 0.1 * index + 0.1,
                    }}
                    variants={fadeUp}
                    key={`company-stats-${index}`}
                  >
                    {stat?.heading && stat?.type === 'standard' && (
                      <h4 className='font-display text-[73px] font-bold leading-[0.8] tracking-tighter text-charcoal'>
                        {stat.heading}
                      </h4>
                    )}
                    {stat?.heading && stat?.type === 'hellopeter' && (
                      <h4 className='font-display text-[73px] font-bold leading-[0.8] tracking-tighter text-charcoal'>
                        {`${stat.heading}+`}
                      </h4>
                    )}
                    {stat?.description && (
                      <h4 className='max-w-[260px] text-base font-medium text-charcoal sm:max-w-[unset] 2xl:text-lg'>
                        <PortableText
                          components={components}
                          value={stat.description}
                        ></PortableText>
                      </h4>
                    )}
                  </m.div>
                ))}
            </div>
          </div>

          {/* Pattern */}
          <m.div
            initial='hidden'
            variants={fadeUp}
            transition={{
              delay: 0.3,
              duration: 0.3,
              type: 'tween',
            }}
            className='absolute z-0 hidden h-48 w-40 opacity-20 xl:block 2xl:opacity-100'
            animate={isInView ? 'visible' : 'hidden'}
          >
            <svg
              xmlns='http://www.w3.org/2000/svg'
              className='w-full'
              width='170'
              height='172'
              viewBox='0 0 170 172'
              fill='none'
            >
              <path
                d='M142.717 89.9211H155.025V101.863H142.717V89.9211ZM129.816 103.501H142.123V115.115H129.816V103.501ZM116.259 116.734H128.566V129.146H116.259V116.734ZM88.5098 144.017H100.818V157.106H88.5098V144.017ZM75.0348 129.7H87.6087V143.157H75.0348V129.7ZM61.6211 116.447H73.9289V128.86H61.6211V116.447ZM48.5557 104.034H60.3105V114.829H48.5557V104.034ZM59.9419 89.1427H48.187V76.9143H59.9419V89.1427ZM73.5603 75.2756H61.2525V62.8628H73.5603V75.2756ZM87.2401 62.0229H74.6662V48.5655H87.2401V62.0229ZM100.449 47.7052H88.1412V34.6164H100.449V47.7052ZM114.416 61.7362H102.108V48.5655H114.416V61.7362ZM128.198 74.9888H115.89V62.576H128.198V74.9888ZM115.501 101.822C115.501 101.822 115.358 101.883 115.276 101.924V89.9006H127.85V101.842H115.501V101.822ZM102.374 115.075V103.747H114.723C114.723 103.747 114.866 103.686 114.948 103.645V115.075H102.374ZM75.1782 103.747H87.4859V114.808H75.1782V103.747ZM87.1173 88.856H74.8095V76.8938H87.1173V88.856ZM88.0798 90.1464H100.388V101.535H88.0798V90.1464ZM114.559 88.856H101.985V76.6275H114.559V88.856ZM101.002 75.2756H88.4279V62.5965H101.002V75.2756ZM61.5187 90.1669H73.1917V101.556H61.5187V90.1669ZM88.7965 116.447H101.371V129.126H88.7965V116.447ZM141.755 88.5897H129.447V76.6275H141.755V88.5897ZM34.4048 90.0645H46.6102V101.658H34.4048V90.0645ZM114.784 143.157H102.476V129.986H114.784V143.157ZM169.36 103.481C169.667 103.481 169.913 103.235 169.913 102.928V89.1427C169.913 88.8355 169.667 88.5897 169.36 88.5897H156.643V75.5624C156.643 75.2551 156.397 75.0093 156.09 75.0093H143.086V62.3097C143.086 62.0025 142.84 61.7567 142.533 61.7567H129.304V48.2787C129.304 47.9715 129.058 47.7257 128.751 47.7257H115.337V34.2477C115.337 33.9405 115.091 33.6947 114.784 33.6947H101.985V20.2986C101.985 19.9914 101.739 19.7456 101.432 19.7456H87.6702C87.363 19.7456 87.1173 19.9914 87.1173 20.2986V33.6947H73.8265C73.5193 33.6947 73.2736 33.9405 73.2736 34.2477V47.992H60.3514C60.0442 47.992 59.7986 48.2378 59.7986 48.545V62.0229H46.9378C46.6306 62.0229 46.3848 62.2688 46.3848 62.576V75.1732H33.8723C33.5651 75.1732 33.3195 75.419 33.3195 75.7262V89.3066H20.1311C19.8239 89.3066 19.5781 89.5524 19.5781 89.8597V103.645C19.5781 103.952 19.8239 104.198 20.1311 104.198H33.6881V115.976C33.6881 116.283 33.9337 116.529 34.2409 116.529H46.7536V129.126C46.7536 129.433 46.9992 129.679 47.3064 129.679H60.1672V143.157C60.1672 143.464 60.4128 143.71 60.72 143.71H73.6423V157.454C73.6423 157.762 73.8879 158.007 74.1951 158.007H87.4859V171.403C87.4859 171.711 87.7316 171.956 88.0388 171.956H101.821C102.128 171.956 102.374 171.711 102.374 171.403V158.007H115.173C115.48 158.007 115.726 157.762 115.726 157.454V143.976H129.14C129.447 143.976 129.693 143.731 129.693 143.423V129.945H142.922C143.229 129.945 143.475 129.699 143.475 129.392V116.693H156.479C156.786 116.693 157.032 116.447 157.032 116.14V103.461H169.381L169.36 103.481Z'
                fill='#D64C19'
              />
              <path
                d='M26.2136 25.4736H14.043V14.0564H26.2136V25.4736ZM0.515865 27.2331H13.1641V38.9754C13.1641 39.2623 13.3934 39.4918 13.68 39.4918H26.653C26.9396 39.4918 27.1689 39.2623 27.1689 38.9754V26.296H39.6834C39.97 26.296 40.1992 26.0665 40.1992 25.7796V12.7751C40.1992 12.4882 39.97 12.2587 39.6834 12.2587H27.0351V0.516358C27.0351 0.229492 26.8059 0 26.5193 0H13.5462C13.2596 0 13.0304 0.229492 13.0304 0.516358V13.1958H0.515865C0.229273 13.1958 0 13.4253 0 13.7122V26.6976C0 26.9845 0.229273 27.214 0.515865 27.214'
                fill='#D64C19'
              />
            </svg>
          </m.div>
        </div>
      </section>
    </>
  );
}
